// Considered: inline per-surface filtering — rejected because the two surfaces diverge (abstract-only guard vs. templateFor gate), sharing a single source prevents drift. See spec Reuse Audit.
import { getEligibleSchemaObjects } from '@center-platform/spheres/identity';
import type { Schema } from 'types/schema';

export function isCenter(sphere: { type: string }): boolean {
  return sphere.type === 'center_organization';
}

/**
 * Returns the subset of `availableTypes` that are eligible for creation as
 * children of `sphere`, evaluated through each candidate's `assignee_filter`
 * for the relevant perspectives.
 *
 * Non-center parents use only the `center_parent` perspective.
 * Center parents (type === 'center_organization') use `center_parent` OR
 * `center_center` — one qualifying perspective suffices (OR / ANY semantics).
 *
 * Abstract and hidden schemas are excluded by the underlying evaluator.
 *
 * @param allSchemas  Full schema pool passed to the evaluator. MUST include
 *   sphere's own schema (by system_name) — the evaluator returns [] otherwise.
 *   Callers inject the parent schema (e.g. via `useSchemaType`) because the
 *   available-types payload only carries child types.
 * @param availableTypes  API-filtered subset to intersect with the eligible set.
 * @param sphere  The parent sphere being created under.
 */
export function getCreatableTypes(
  allSchemas: Schema[],
  availableTypes: Schema[],
  sphere: { type: string }
): Schema[] {
  // An empty assignee type makes the evaluator skip assignee matching and treat
  // every candidate as eligible — wrong. Without a known parent type, nothing is creatable.
  if (!sphere.type) return [];

  const perspectiveSystemNames = isCenter(sphere)
    ? ['center_parent', 'center_center']
    : ['center_parent'];

  const eligible = getEligibleSchemaObjects(allSchemas, {
    assigneeSystemName: sphere.type,
    perspectiveSystemNames,
    enforcePerspectiveMatch: true,
  });

  const availableSet = new Set(availableTypes.map(s => s.system_name.toLowerCase()));

  // Intersect with availableTypes AND de-duplicate by system_name. Callers inject
  // the parent's own schema into `allSchemas`; if the parent type is itself an
  // eligible child (self-referential, e.g. an org under an org) the evaluator can
  // emit it twice — collapse to one so chips/keys stay unique.
  const seen = new Set<string>();
  const result: Schema[] = [];
  for (const { schema } of eligible) {
    const key = schema.system_name.toLowerCase();
    if (availableSet.has(key) && !seen.has(key)) {
      seen.add(key);
      result.push(schema);
    }
  }
  return result;
}
